import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Check, X, ArrowRight, Clock, User, Building, Loader2, Pencil, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import TalepGecmisiModal from '@/components/TalepGecmisiModal';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Onaylanmamis talep duzeltilebilir ve silinebilir
const DEGISTIRILEBILIR = ['draft', 'pending_cost_control', 'pending_manager', 'rejected'];

const statusLabels = {
  draft: 'Taslak',
  pending_cost_control: 'Maliyet Kontrol Onayı Bekliyor',
  pending_manager: 'Yönetici Onayı Bekliyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  ordered: 'Sipariş Verildi'
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_cost_control: 'bg-yellow-100 text-yellow-800',
  pending_manager: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  ordered: 'bg-blue-100 text-blue-800'
};

export default function PurchaseRequestDetail() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('purchasing', 'delete');
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/purchasing');
  const [request, setRequest] = useState(null);
  usePageTitle('Talep Detayı');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [gecmisAcik, setGecmisAcik] = useState(false);
  const [silinsinMi, setSilinsinMi] = useState(false);

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/purchase-requests/${id}`);
      toast.success('Talep silindi');
      navigate('/erp/purchasing?tab=requests');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Talep silinemedi');
    } finally {
      setActionLoading(false);
      setSilinsinMi(false);
    }
  };

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/purchase-requests/${id}`);
      setRequest(res.data);
    } catch (err) {
      toast.error('Talep yüklenemedi');
      navigate('/erp/purchasing');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/purchase-requests/${id}/submit`);
      toast.success('Talep onaya gönderildi');
      fetchRequest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveCostControl = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/purchase-requests/${id}/approve-cost-control`);
      toast.success('Maliyet kontrol onayı verildi');
      fetchRequest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveManager = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/purchase-requests/${id}/approve-manager`);
      toast.success('Yönetici onayı verildi');
      fetchRequest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/purchase-requests/${id}/reject`, { reason: 'Reddedildi' });
      toast.success('Talep reddedildi');
      fetchRequest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!request) return null;

  // Talebe uygulanabilir islemler. Liste sayfasinda aksiyon butonu YOK;
  // hepsi burada tek bir "Islemler" menusunde toplanir.
  const islemler = [];
  if (DEGISTIRILEBILIR.includes(request.status)) {
    islemler.push({
      anahtar: 'duzenle', etiket: 'Düzenle', ikon: Pencil,
      calistir: () => navigate(`/erp/purchase-requests/${id}/edit`),
    });
  }
  if (request.status === 'draft') {
    islemler.push({ anahtar: 'gonder', etiket: 'Onaya Gönder', ikon: ArrowRight, calistir: handleSubmit });
  }
  if (request.status === 'pending_cost_control') {
    islemler.push({ anahtar: 'maliyet-onay', etiket: 'Maliyet Onayla', ikon: Check, calistir: handleApproveCostControl });
    islemler.push({ anahtar: 'reddet', etiket: 'Reddet', ikon: X, calistir: handleReject, tehlikeli: true });
  }
  if (request.status === 'pending_manager') {
    islemler.push({ anahtar: 'yonetici-onay', etiket: 'Yönetici Onayla', ikon: Check, calistir: handleApproveManager });
    islemler.push({ anahtar: 'reddet', etiket: 'Reddet', ikon: X, calistir: handleReject, tehlikeli: true });
  }
  if (DEGISTIRILEBILIR.includes(request.status) && silmeYetkisi) {
    islemler.push({
      anahtar: 'sil', etiket: 'Sil', ikon: Trash2, ayrac: true, tehlikeli: true,
      calistir: () => setSilinsinMi(true),
    });
  }

  return (
    <div className="p-6 space-y-6" data-testid="purchase-request-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1a1916]">Satın Alma Talebi</h1>
            <p className="text-sm text-[#8a7e70]">#{request.id?.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setGecmisAcik(true)}
            className="border-[#d0c4b0] bg-white text-[#3F3E39] hover:bg-[#e8dfd0]"
            data-testid="talep-gecmis-btn">
            <Clock size={16} className="mr-2" /> Geçmiş
          </Button>
          {islemler.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={actionLoading}
                  className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
                  data-testid="talep-islemler-btn">
                  {actionLoading
                    ? <Loader2 size={16} className="mr-2 animate-spin" />
                    : null}
                  İşlemler <ChevronDown size={15} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#faf7f2] border-[#d0c4b0] w-52">
                {islemler.map((islem) => (
                  <div key={islem.anahtar}>
                    {islem.ayrac && <DropdownMenuSeparator className="bg-[#e3dbcd]" />}
                    <DropdownMenuItem
                      onClick={islem.calistir}
                      disabled={actionLoading}
                      className={`cursor-pointer ${islem.tehlikeli
                        ? 'text-[#7a3535] focus:bg-[#7a3535]/10 focus:text-[#7a3535]'
                        : 'text-[#3F3E39] focus:bg-[#e8dfd0]'}`}
                      data-testid={`talep-islem-${islem.anahtar}`}>
                      <islem.ikon size={15} className="mr-2" /> {islem.etiket}
                    </DropdownMenuItem>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Badge className={statusColors[request.status]}>{statusLabels[request.status]}</Badge>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <Clock size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Oluşturulma Tarihi</p>
                <p className="font-semibold text-[#1a1916]">
                  {new Date(request.created_at).toLocaleDateString('tr-TR', { 
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <Building size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Maliyet Merkezi</p>
                <p className="font-semibold text-[#1a1916]">{request.cost_center_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <User size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Talep Eden</p>
                <p className="font-semibold text-[#1a1916]">{request.requested_by_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approval History */}
      {request.approval_history && request.approval_history.length > 0 && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Onay Geçmişi</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {request.approval_history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#e8dfd0]/50 rounded-lg">
                  <div className={`p-1.5 rounded-full ${h.status === 'approved' ? 'bg-green-100' : 'bg-red-100'}`}>
                    {h.status === 'approved' ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <X size={14} className="text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#1a1916]">{h.step}</p>
                    <p className="text-sm text-[#8a7e70]">{h.user}</p>
                    {h.reason && <p className="text-sm text-red-600 mt-1">Sebep: {h.reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916]">Talep Kalemleri</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#e8dfd0]">
                <TableHead className="text-[#1a1916] font-bold">Ürün Kodu</TableHead>
                <TableHead className="text-[#1a1916] font-bold">Ürün Adı</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-right">Miktar</TableHead>
                <TableHead className="text-[#1a1916] font-bold">Birim</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-right">Mevcut Stok</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-right">Son Fiyat</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-right">Tahmini Tutar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {request.items?.map((item, i) => (
                <TableRow key={i} className="hover:bg-[#f5f0e8]">
                  <TableCell className="font-mono">{item.product_code || '-'}</TableCell>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell className="text-right font-semibold">{item.requested_quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  {/* Stok ana birimde tutulur; kalem alternatif birimdeyse
                      SEÇİLİ birimde gösterilir, ana birim karşılığı altta. */}
                  <TableCell className="text-right">
                    {item._miktar_gizli ? (
                      <span className="text-[#8a7e70]">-</span>
                    ) : (
                      <>
                        <div>
                          {(item.current_stock_secili ?? item.current_stock ?? 0).toLocaleString('tr-TR',
                            { maximumFractionDigits: 2 })} {item.unit}
                        </div>
                        {item.base_unit && item.base_unit !== item.unit && (
                          <div className="text-[11px] text-[#8a7e70]">
                            {(item.current_stock ?? 0).toLocaleString('tr-TR',
                              { maximumFractionDigits: 2 })} {item.base_unit}
                          </div>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">{(item.last_purchase_price || 0).toFixed(2)} TRY/{item.unit}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{(item.estimated_cost || 0).toFixed(2)} TRY</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="p-4 border-t border-[#d0c4b0] flex justify-end">
            <div className="text-right">
              <p className="text-sm text-[#8a7e70]">Toplam Tahmini Tutar</p>
              <p className="text-2xl font-bold text-[#1a1916]">
                {(request.total_estimated_cost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TRY
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {request.notes && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Notlar</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-[#3F3E39]">{request.notes}</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={silinsinMi} onOpenChange={setSilinsinMi}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Talep silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {request.items?.length || 0} kalemlik bu talep silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-[#7a3535] hover:bg-[#5e2929]" data-testid="confirm-delete-request">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TalepGecmisiModal open={gecmisAcik} onOpenChange={setGecmisAcik} requestId={id} />
    </div>
  );
}
